<template>
  <MainLayout>
    <div class="container public-form-page">
      <div class="public-form-page__hero">
        <h2 class="title text-uppercase mb-12">{{ copy.title }}</h2>
        <p class="public-form-page__intro">{{ copy.intro }}</p>
      </div>

      <div class="public-form-page__card text-left">
        <div class="public-form-page__card-head">
          <h4 class="mb-10">{{ copy.formTitle }}</h4>
          <p class="public-form-page__form-intro">{{ copy.formIntro }}</p>
        </div>

        <el-form
          ref="form"
          :model="form"
          :rules="rules"
          label-position="top"
        >
          <el-row :gutter="20">
            <el-col :xs="24" :md="12">
              <el-form-item :label="copy.fields.firstName" prop="firstName">
                <el-input v-model="form.firstName" />
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item :label="copy.fields.lastName" prop="lastName">
                <el-input v-model="form.lastName" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-row :gutter="20">
            <el-col :xs="24" :md="12">
              <el-form-item :label="copy.fields.customerType" prop="customerType">
                <el-select
                  v-model="form.customerType"
                  class="public-form-page__full-width"
                  :placeholder="copy.customerTypePlaceholder"
                >
                  <el-option
                    :label="copy.options.professional"
                    value="PROFESSIONAL"
                  />
                  <el-option
                    :label="copy.options.individual"
                    value="INDIVIDUAL"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :xs="24" :md="12">
              <el-form-item :label="copy.fields.cellNumber" prop="cellNumber">
                <el-input v-model="form.cellNumber" />
              </el-form-item>
            </el-col>
          </el-row>

          <el-form-item :label="copy.fields.email" prop="email">
            <el-input v-model="form.email" />
          </el-form-item>

          <el-form-item :label="copy.fields.details" prop="details">
            <el-input v-model="form.details" type="textarea" :rows="6" />
          </el-form-item>

          <el-button
            type="primary"
            :loading="isSubmitting"
            @click="submit"
          >
            {{ copy.submit }}
          </el-button>
        </el-form>
      </div>
    </div>
  </MainLayout>
</template>

<script>
import { Notification } from "element-ui";
import { mapState } from "vuex";
import MainLayout from "@/components/layout/main-layout.vue";
import publicIntakeApi from "@/api/public-intake";
import { publicFormsContent } from "@/content/public-pages-content";
import {
  getCurrentMarket,
  normalizeLocale,
} from "@/utils/market-utils";

const emailPattern = /\S+@\S+\.\S+/;
const phonePattern = /^[0-9+()\-\s.]{7,}$/;

export default {
  components: {
    MainLayout,
  },
  data() {
    return {
      form: {
        firstName: "",
        lastName: "",
        customerType: "",
        email: "",
        cellNumber: "",
        details: "",
      },
      isSubmitting: false,
    };
  },
  computed: {
    ...mapState("configuration", ["actionBar"]),
    market() {
      return getCurrentMarket(this.actionBar && this.actionBar.country);
    },
    localeKey() {
      return normalizeLocale(this.$i18n.locale);
    },
    copy() {
      return publicFormsContent.contact[this.localeKey];
    },
    validationMessages() {
      return this.$i18n.t("users.deliveryAddress");
    },
    rules() {
      const required = this.validationMessages.formRequiredMsg;
      const pattern = this.validationMessages.formPatternMsg;

      return {
        firstName: [{ required: true, message: required.firstName }],
        lastName: [{ required: true, message: required.lastName }],
        customerType: [{ required: true, message: this.copy.fields.customerType }],
        email: [
          { required: true, message: required.email },
          {
            pattern: emailPattern,
            message: pattern.email,
          },
        ],
        cellNumber: [
          { required: true, message: required.phone },
          {
            pattern: phonePattern,
            message: pattern.phone,
          },
        ],
        details: [{ required: true, message: this.copy.fields.details }],
      };
    },
  },
  methods: {
    resetForm() {
      this.form = {
        firstName: "",
        lastName: "",
        customerType: "",
        email: "",
        cellNumber: "",
        details: "",
      };
      this.$nextTick(() => {
        this.$refs.form.clearValidate();
      });
    },
    submit() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) {
          return;
        }

        this.isSubmitting = true;

        try {
          await publicIntakeApi.submitContact({
            market: this.market,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            customerType: this.form.customerType,
            email: this.form.email,
            cellNumber: this.form.cellNumber,
            details: this.form.details,
            source: "contact-us",
          });
          Notification({
            title: "Success",
            message: this.copy.success,
            type: "success",
          });
          this.resetForm();
        } catch (error) {
          Notification({
            title: "Error",
            message:
              error && error.message === "PUBLIC_INTAKE_API_NOT_CONFIGURED"
                ? this.copy.configError
                : this.copy.error,
            type: "error",
          });
        } finally {
          this.isSubmitting = false;
        }
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

.public-form-page {
  padding-top: 56px;
  padding-bottom: 72px;
}

.public-form-page__hero {
  max-width: 760px;
  margin: 0 0 40px;
  text-align: left;
}

.public-form-page__intro {
  line-height: 1.7;
  margin: 0;
}

.public-form-page__card-head {
  max-width: 640px;
  margin-bottom: 28px;
}

.public-form-page__form-intro {
  line-height: 1.7;
  margin: 0;
}

::v-deep .public-form-page__card .el-select .el-input__inner {
  height: 40px;
  border: 1px solid #dcdfe6 !important;
  color: #000;
  font-family: $font-family-futura-std;
  font-size: 14px;
  line-height: 40px;
}

::v-deep .public-form-page__card .el-select .el-input__suffix {
  right: 12px;
}

::v-deep .public-form-page__card .el-select .el-input__icon {
  color: #272421;
  font-weight: 400;
  line-height: 40px;
}

.public-form-page__card {
  max-width: 900px;
  margin: 0;
  padding: 36px 40px;
  border-radius: 24px;
  background: #faf8f6;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.04);
}

.public-form-page__full-width {
  width: 100%;
}

@media screen and (max-width: $media-md) {
  .public-form-page {
    padding-top: 36px;
    padding-bottom: 48px;
  }

  .public-form-page__hero {
    margin-bottom: 28px;
    text-align: left;
  }

  .public-form-page__card {
    padding: 20px;
  }
}
</style>
